<script setup lang="ts">
import type { InputProps } from '~kui/types/input';

interface Props extends InputProps {
  errorMessages?: string[];
  disabled?: boolean;
  htmlLabel?: boolean;
  dataTestId?: string;
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  errorMessages: () => [],
  hideLabel: false,
  htmlLabel: false,
  dataTestId: undefined,
});

const emit = defineEmits<{ blur: [event: FocusEvent] }>();
const value = defineModel<boolean>({ default: false });
const elementId = useId();
const hasError = computed(() => props.errorMessages.length > 0);
const errorsId = computed(() => `error-${elementId}`);
const variantClasses = computed<string>(() => {
  return twMerge(
    cva('size-5 cursor-pointer rounded-md p-2 align-middle accent-secondary', {
      variants: {
        disabled: {
          true: 'accent-primary opacity-50 border-input bg-muted',
        },
        hasError: {
          true: 'border-destructive',
        },
      },
    })({
      disabled: props.disabled,
      hasError: props.errorMessages.length > 0,
    }),
  );
});

const textClasses = computed<string>(() => {
  return twMerge(
    cva('ml-3 cursor-pointer', {
      variants: {
        disabled: {
          true: 'text-muted-foreground',
        },
        hasError: {
          true: 'text-destructive',
        },
      },
    })({
      disabled: props.disabled,
      hasError: props.errorMessages.length > 0,
    }),
  );
});

const checkboxContainer = ref<HTMLElement | undefined>(undefined);
const onFocusOut = (event: FocusEvent) => {
  if (!checkboxContainer.value?.contains(event.relatedTarget as HTMLElement)) {
    emit('blur', event);
  }
};
</script>

<template>
  <div>
    <div
      ref="checkboxContainer"
      class="flex items-center"
      @focusout="onFocusOut"
    >
      <input
        :id="elementId"
        v-model="value"
        type="checkbox"
        :data-testid="dataTestId"
        :class="variantClasses"
        :aria-required="required"
        :aria-invalid="hasError ? 'true' : undefined"
        :aria-describedby="hasError ? errorsId : undefined"
        :disabled="disabled"
      >
      <label
        v-if="!hideLabel"
        :class="textClasses"
        :for="elementId"
      >
        <slot v-if="$slots.customLabel" name="customLabel" />
        <KUICoreHtml
          v-else-if="htmlLabel"
          as="span"
          :html="label ?? ''"
        />
        <span v-else>{{ label }}</span>
        <sup v-if="required" class="text-destructive">*</sup>
      </label>
    </div>
    <KUICoreInputErrors
      :id="errorsId"
      :error-messages="errorMessages"
      :font-size="errorMessageSize"
    />
  </div>
</template>
